import { server } from "../../server";
import { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { fetchExpenses } from "../../redux/expensesSlice";
import { useDispatch, useSelector } from "react-redux";

function ExpenseDetails() {
  const expenseHeads = [
    "Electricity Bill",
    "Water Bill",
    "Telephone Bill",
    "Internet Charges",
    "Maintenance & Repairs",
    "Building Rent / Lease",
    "Transportation",
    "Security & Safety",
    "Office Supplies & Stationery",
    "Teaching Materials",
    "Library Expenses",
    "Sports Equipment & Activities",
    "Laboratory Equipment & Chemicals",
    "Furniture & Fixtures",
    "Printing & Publications",
    "Software & IT Maintenance",
    "Insurance",
    "Medical & First Aid Expenses",
    "Examination Expenses",
    "Scholarships & Fee Concessions",
    "Affiliation & Accreditation Fees",
    "Legal & Professional Fees",
    "Audit & Accounting Fees",
    "Marketing & Advertisement",
    "Student Welfare Activities",
    "Staff Training & Development",
    "Cultural & Annual Events",
    "Excursion & Field Trips",
    "Mess & Canteen Expenses",
    "School Uniforms & Dress Code",
    "Festival Celebrations",
    "Science & Tech Fair Expenses",
    "Petty Cash Expenses",
    "Bank Charges & Loan Interests",
    "Donations & Charity Contributions",
    "Miscellaneous",
    "Other",
  ];

  const dispatch = useDispatch();
  const { data: expenses, loading: fetchLoading } = useSelector(
    (state) => state.expenses,
  );

  useEffect(() => {
    dispatch(fetchExpenses());
  }, [dispatch]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const [isEditing, setIsEditing] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState(null);

  const [expenseData, setExpenseData] = useState({
    expenseHead: "",
    name: "",
    invoiceNumber: "",
    date: "",
    amount: "",
    description: "",
  });
  const [file, setFile] = useState(null);

  const handleChange = (e) => {
    setExpenseData({ ...expenseData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setExpenseData({
      expenseHead: "",
      name: "",
      invoiceNumber: "",
      date: "",
      amount: "",
      description: "",
    });
    setFile(null);
  };

  const handleEditClick = (expense) => {
    setExpenseData({
      expenseHead: expense.expenseHead || "",
      name: expense.name || "",
      invoiceNumber: expense.invoiceNumber || "",
      date: expense.date ? expense.date.split("T")[0] : "",
      amount: expense.amount || "",
      description: expense.description || "",
      document: expense.document || "",
    });
    setEditingExpenseId(expense.expenseId);
    setIsEditing(true);

    const modal = new window.bootstrap.Modal(document.querySelector(".modal"));
    modal.show();
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file && !isEditing) {
      toast.error("Select File", { position: "top-right" });
      return;
    }

    try {
      setLoading(true);
      let key = null;

      if (file) {
        const presignedRes = await axios.post(
          "https://7otfgyv0ke.execute-api.ap-south-1.amazonaws.com/prod/expense_presigned",
          { fileName: [file.name] },
        );

        const {
          signedUrl,
          key: uploadedKey,
          contentType,
        } = presignedRes.data["body-json"].body.uploadDetails[0];

        await axios.put(signedUrl, file, {
          headers: {
            "Content-Type": contentType,
          },
        });

        key = uploadedKey;
      }

      const payload = {
        ...expenseData,
        ...(file
          ? { document: key }
          : isEditing && expenseData.document
            ? { document: expenseData.document }
            : {}),
        ...(isEditing && { expenseId: editingExpenseId }),
      };

      const endpoint = isEditing
        ? "update_expense_mongodb"
        : "add_expense_mongodb";

      await axios.post(
        `https://7otfgyv0ke.execute-api.ap-south-1.amazonaws.com/prod/${endpoint}`,
        payload,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        },
      );

      toast.success(`Expense ${isEditing ? "Updated" : "Added"}`, {
        position: "top-right",
      });

      const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
      if (closeButton) closeButton.click();

      setExpenseData({
        expenseHead: "",
        name: "",
        invoiceNumber: "",
        date: "",
        amount: "",
        description: "",
      });
      setFile(null);
      setIsEditing(false);
      setEditingExpenseId(null);

      const response = await axios.post(
        `${server}/expenses_getall_and_get_by_date_mongodb`,
        {},
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        },
      );
      if (response.data && response.data["body-json"]?.body?.data) {
        dispatch(fetchExpenses());
      }
    } catch (error) {
      console.error(error);
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  // Filter
  const filteredRecords = expenses.filter((expense) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(expense).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower),
        );
      }
      return false;
    });
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const columnMapping = {
    "studentDetails.admission_no": "Admission No",
    "studentDetails.name": "Name",
    "studentDetails.currentClass": "Class",
    "studentDetails.currentSection": "Section",
    "studentDetails.rollNo": "Roll No",
    "studentDetails.phoneNo": "Phone No",
    "studentDetails.gender": "Gender",
    "transportFacility.busStop": "Bus Stop",
    "transportFacility.busNumber": "Bus Number",
    "transportFacility.vehicleNo": "Vehicle No",
    "transportFacility.applicableMonth": "Applicable Month",
    "scholarship.type": "Type",
    "scholarship.siblingAdmissionNo": "Sibling Admission No",
    "scholarship.employeeId": "Employee Id",
    "scholarship.feeDiscount.admissionFee": "Admission Fee",
    "scholarship.feeDiscount.monthlyFee": "Monthly Fee",
    "scholarship.feeDiscount.developmentCharges": "Development Charges",
  };

  const handleDelete = async (expenseIdToDelete) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to delete expense ID "${expenseIdToDelete}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${server}/delete_expense_mongodb`,
          { expenseId: expenseIdToDelete },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.status === 200) {
          toast.success("Expense Deleted", { position: "top-right" });
          fetchExpenses();
        } else {
          toast.error("Deletion failed. Please try again.");
        }
      } catch (error) {
        toast.error("Error occurred while deleting. Try again.");
      }
    }
  };

  const handleExportExpenseExcel = () => {
    if (!expenses || expenses.length === 0) {
      toast.error("No expense data to export");
      return;
    }

    const sheetData = expenses.map((expense) => ({
      "Expense ID": expense.expenseId || "",
      "Expense Head": expense.expenseHead || "",
      Name: expense.name || "",
      "Invoice Number": expense.invoiceNumber || "",
      Date: expense.date
        ? new Date(expense.date).toLocaleDateString("en-GB")
        : "",
      Amount: expense.amount || "",
      Description: expense.description || "",
      "Document Link": expense.document || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Expense Report");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Expense_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportExpenseHeadWiseExcel = () => {
    if (!expenses || expenses.length === 0) {
      toast.error("No expense data to export");
      return;
    }

    const groupedExpenses = expenses.reduce((acc, expense) => {
      const head = expense.expenseHead || "Unknown";
      if (!acc[head]) acc[head] = [];
      acc[head].push({
        "Expense ID": expense.expenseId || "",
        "Expense Head": expense.expenseHead || "",
        Name: expense.name || "",
        "Invoice Number": expense.invoiceNumber || "",
        Date: expense.date
          ? new Date(expense.date).toLocaleDateString("en-GB")
          : "",
        Amount: expense.amount || "",
        Description: expense.description || "",
        "Document Link": expense.document || "",
      });
      return acc;
    }, {});

    const workbook = XLSX.utils.book_new();

    Object.entries(groupedExpenses).forEach(([head, records]) => {
      const worksheet = XLSX.utils.json_to_sheet(records);
      const safeSheetName = head.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Expense_HeadWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportExpenseIdWiseAmountExcel = () => {
    if (!expenses || expenses.length === 0) {
      toast.error("No expense data to export");
      return;
    }

    const sheetData = expenses.map((expense) => ({
      "Expense ID": expense.expenseId || "",
      Amount: expense.amount || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Expense ID & Amount");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Expense_IdWiseAmount_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div
            class="modal fade gd-example-modal-lg"
            tabindex="-1"
            role="dialog"
            aria-labelledby="myLargeModalLabel"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header d-flex justify-content-between">
                    <h5
                      className="modal-title fs-4"
                      id="exampleModalToggleLabel"
                    >
                      Add Expense
                    </h5>
                    <div className="d-flex align-items-center">
                      <span className="badge bg-success-soft me-3 px-3">
                        Editable
                      </span>
                      <div className="form-check form-switch me-4">
                        <input
                          type="checkbox"
                          className="form-check-input m-0"
                          checked
                          readOnly
                        />
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      ></button>
                    </div>
                  </div>

                  <div className="modal-body">
                    <div className="row g-2 mb-2">
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Expense Head <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={expenseHeads.map((head) => ({
                            value: head,
                            label: head,
                          }))}
                          name="expenseHead"
                          placeholder="Select Expense Head"
                          value={
                            expenseHeads
                              .filter(
                                (head) => head === expenseData.expenseHead,
                              )
                              .map((head) => ({
                                value: head,
                                label: head,
                              }))[0] || null
                          }
                          onChange={(selectedOption) =>
                            handleChange({
                              target: {
                                name: "expenseHead",
                                value: selectedOption?.value,
                              },
                            })
                          }
                        />
                      </div>

                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="name"
                          value={expenseData.name}
                          onChange={handleChange}
                          placeholder="Enter Name"
                          required
                        />
                      </div>

                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Invoice Number <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="invoiceNumber"
                          value={expenseData.invoiceNumber}
                          onChange={handleChange}
                          placeholder="Enter Invoice Number"
                          required
                        />
                      </div>

                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Date <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          className="form-control text-dark"
                          name="date"
                          value={expenseData.date}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Amount <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className="form-control text-dark"
                          name="amount"
                          value={expenseData.amount}
                          onChange={handleChange}
                          placeholder="Enter Amount"
                          required
                        />
                      </div>

                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Attach Document{" "}
                          {!isEditing && <span className="text-danger">*</span>}
                          {"  "}&nbsp;
                        </label>
                        <input
                          type="file"
                          className="form-control text-dark"
                          onChange={handleFileChange}
                          accept="application/pdf,image/*"
                          required={!isEditing}
                        />
                      </div>

                      <div className="col-12">
                        <label className="form-label mb-1 ms-3">
                          Description <span className="text-danger">*</span>
                        </label>
                        <textarea
                          className="form-control text-dark"
                          name="description"
                          value={expenseData.description}
                          onChange={handleChange}
                          rows="3"
                          placeholder="Enter Description"
                          required
                        ></textarea>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer d-flex justify-content-between py-2">
                    <h5 className="fs-4">
                      <span className="text-dark">ID — 040041</span>
                    </h5>
                    <div>
                      <button
                        type="button"
                        className="btn btn-secondary px-3 me-1 rounded-pill"
                        data-bs-dismiss="modal"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                      <button
                        type="submit"
                        class="btn btn-primary px-5 rounded-pill fw-normal"
                        disabled={loading}
                      >
                        {loading ? (
                          <div
                            class="spinner-border spinner-border-sm"
                            role="status"
                          ></div>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((expense, index) => (
                          <tr>
                            <td>1.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span class="text-dark">Expense ID</span> &mdash;{" "}
                              {expense.expenseId}
                              <br />
                              <span class="text-dark">Expense Head</span>{" "}
                              &mdash; {expense.expenseHead}
                              <br />
                              <span class="text-dark">Name</span> &mdash;{" "}
                              {expense.name}
                              <br />
                              <span class="text-dark">Invoice Number</span>{" "}
                              &mdash; {expense.invoiceNumber} <br />
                              <span class="text-dark">Date</span> &mdash;{" "}
                              {expense.date
                                ? new Date(expense.date).toLocaleDateString(
                                    "en-GB",
                                  )
                                : ""}
                              <br />
                              <span class="text-dark">Amount</span> &mdash;{" "}
                              {expense.amount} <br />
                              <span class="text-dark">
                                Attacted Document
                              </span>{" "}
                              &mdash;{" "}
                              <a href={expense.document} target="_blank">
                                View File
                              </a>{" "}
                              <br />
                              <span class="text-dark">
                                Description
                              </span> &mdash; {expense.description}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <span
                                  onClick={() => handleEditClick(expense)}
                                  class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Update
                                </span>
                                <span
                                  onClick={() =>
                                    handleDelete(expense.expenseId)
                                  }
                                  class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Delete
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="d-flex bg-white justify-content-between rounded-4">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                        <Link
                          data-bs-toggle="modal"
                          onClick={resetForm}
                          data-bs-target=".gd-example-modal-lg"
                          class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                        >
                          Add Expense
                        </Link>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Expenses</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Expense Details
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {fetchLoading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Expense Details
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              0.2 MB Size
                            </span>
                          </p>
                        </div>
                        <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleExportExpenseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Expense Report
                                </span>
                                <span
                                  onClick={handleExportExpenseHeadWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Expense Head Wise
                                </span>
                                <span
                                  onClick={handleExportExpenseIdWiseAmountExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Expense ID Wise Amount
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default ExpenseDetails;
